import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import {
  SceneShell,
  SlideChip,
  SlideTitle,
  Card3D,
  Punchline,
  DefBox,
  PlainBox,
  Pill,
  glow,
  spAppear,
} from "./shared";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/* ════════════ Slide 07 — EAKC fragmentation ════════════ */
export const S07Fragmented: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonOrange;
  const ap = spAppear(frame, fps);

  const books = [
    { icon: "📝", label: "agent persona", sub: "role_prompt", c: COLORS.neonBlue, x: 150 },
    { icon: "⚙️", label: "workflow", sub: "TypeScript code", c: COLORS.neonPurple, x: 560 },
    { icon: "📋", label: "mission template", sub: "a DB row", c: COLORS.neonPink, x: 970 },
    { icon: "🔧", label: "macro", sub: "TypeScript code", c: COLORS.neonCyan, x: 1380 },
  ];

  return (
    <SceneShell accent={accent} dark>
      <SlideChip n={7} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text="Why the patching never ends"
        subtitle="Four parallel knowledge stores — each edited by hand, none auto-learning"
        color={accent}
        appear={ap}
      />

      {/* four notebooks */}
      {books.map((b, i) => {
        const sp = spring({ frame: frame - 16 - i * 12, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const sc = interpolate(sp, [0, 1], [0.5, 1], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: 292 + Math.sin(frame * 0.03 + i) * 7,
              left: b.x,
              width: 360,
              height: 162,
              opacity: op,
              transform: `scale(${sc}) perspective(1100px) rotateY(${(i - 1.5) * 7}deg)`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 16,
                background: `linear-gradient(140deg, ${b.c}28, ${COLORS.bgCard}f0)`,
                border: `2.4px solid ${b.c}`,
                boxShadow: `0 0 30px ${b.c}55`,
                padding: 20,
                display: "flex",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 58 }}>{b.icon}</div>
              <div>
                <div style={{ fontSize: 23, fontFamily: FONTS.heading, fontWeight: 800, color: b.c, textShadow: glow(b.c) }}>
                  {b.label}
                </div>
                <div style={{ fontSize: 15, fontFamily: FONTS.mono, color: COLORS.textMuted, marginTop: 4 }}>
                  {b.sub}
                </div>
                <div style={{ fontSize: 14, color: COLORS.neonRed, marginTop: 8, fontWeight: 700 }}>
                  ✎ edited by hand
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* arrows down to YOU */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {books.map((b, i) => (
          <line
            key={i}
            x1={b.x + 180}
            y1={462}
            x2={960}
            y2={556}
            stroke={accent}
            strokeWidth={3}
            strokeDasharray="9 8"
            strokeDashoffset={-(frame * 3) % 17}
            strokeOpacity={spAppear(frame, fps, 40 + i * 6) * 0.8}
            style={{ filter: `drop-shadow(0 0 6px ${accent})` }}
          />
        ))}
      </svg>

      {/* YOU bottleneck */}
      <div
        style={{
          position: "absolute",
          top: 556,
          left: "50%",
          transform: "translateX(-50%)",
          opacity: spAppear(frame, fps, 56),
        }}
      >
        <div
          style={{
            padding: "14px 44px",
            borderRadius: 16,
            background: `linear-gradient(135deg, ${COLORS.neonRed}33, ${COLORS.bgCard}f2)`,
            border: `2.5px solid ${COLORS.neonRed}`,
            boxShadow: `0 0 38px ${COLORS.neonRed}77`,
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ fontSize: 44 }}>👤</div>
          <div style={{ fontSize: 28, fontFamily: FONTS.heading, fontWeight: 900, color: COLORS.neonRed }}>
            YOU — the only one allowed to write
          </div>
        </div>
      </div>

      {/* dangling write */}
      <div
        style={{
          position: "absolute",
          top: 668,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 18,
          opacity: spAppear(frame, fps, 72),
        }}
      >
        <Pill text="🔁 improvement-analyzer  ·  proposes fixes" color={COLORS.neonGreen} />
        <div style={{ fontSize: 28, color: COLORS.neonRed }}>⇢</div>
        <div
          style={{
            padding: "10px 22px",
            borderRadius: 12,
            border: `2px dashed ${COLORS.neonRed}`,
            background: `${COLORS.neonRed}12`,
            fontSize: 18,
            fontFamily: FONTS.mono,
            color: COLORS.neonRed,
            fontWeight: 800,
          }}
        >
          📥 proposal queue — ✕ nobody ever reads it
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 54, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <DefBox
          term="Dangling write"
          body="Code that produces output nothing ever consumes. Detection without apply is analytics — not learning."
          color={accent}
          appear={spAppear(frame, fps, 86)}
          width={1100}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 08 — Capability matrix ════════════ */
export const S08Matrix: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonBlue;
  const ap = spAppear(frame, fps);

  // Hermes' 24 tools, sorted into four honest buckets
  const columns = [
    {
      label: "HAVE",
      sub: "already built",
      icon: "✓",
      c: COLORS.neonGreen,
      tools: ["Code Execution", "Task Planning", "Memory", "Clarifying Questions", "Task Delegation", "Cron Jobs"],
    },
    {
      label: "PARTIAL",
      sub: "partly there",
      icon: "◐",
      c: COLORS.neonAmber,
      tools: ["Web Search", "Mixture of Agents", "Skills", "Session Search", "Cross-Platform Messaging"],
    },
    {
      label: "GAP",
      sub: "worth building",
      icon: "○",
      c: COLORS.neonPink,
      tools: ["Vision Analysis", "Video Analysis", "Image Generation", "Video Generation", "X / Twitter Search", "Text-to-Speech"],
    },
    {
      label: "N / A",
      sub: "out of scope",
      icon: "✕",
      c: COLORS.textMuted,
      tools: ["Browser Automation", "Terminal & Processes", "File Operations", "Home Assistant", "Spotify", "Yuanbao", "Computer Use"],
    },
  ];
  const covered = columns[0].tools.length + columns[1].tools.length;
  const counter = Math.round(interpolate(frame, [30, 95], [0, covered], clamp));

  return (
    <SceneShell accent={accent}>
      <SlideChip n={8} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="Hermes → EAKC capability matrix"
        subtitle="Sort Hermes' 24 tools into four honest buckets — before building anything"
        color={accent}
        appear={ap}
      />

      {/* counter pill */}
      <div
        style={{
          position: "absolute",
          top: 232,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          opacity: spAppear(frame, fps, 22),
        }}
      >
        <Pill
          text={`${counter} OF 24 — ALREADY HAVE OR PARTIAL · ROUGHLY HALF`}
          color={COLORS.neonGreen}
        />
      </div>

      {/* four labelled columns */}
      <div
        style={{
          position: "absolute",
          top: 296,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 28,
          perspective: 1900,
        }}
      >
        {columns.map((col, ci) => {
          const sp = spring({ frame: frame - 14 - ci * 12, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          const sc = interpolate(sp, [0, 1], [0.62, 1], clamp);
          const floatY = Math.sin(frame * 0.025 + ci) * 5;
          return (
            <div
              key={ci}
              style={{
                width: 420,
                height: 478,
                opacity: op,
                transform: `translateY(${floatY}px) scale(${sc}) perspective(1600px) rotateY(${(ci - 1.5) * 7}deg)`,
                transformStyle: "preserve-3d",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 20,
                  background: `linear-gradient(160deg, ${col.c}20, ${COLORS.bgCard}f4)`,
                  border: `2.4px solid ${col.c}`,
                  boxShadow: `0 0 34px ${col.c}44, 0 20px 40px rgba(0,0,0,0.5)`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* header */}
                <div
                  style={{
                    background: `${col.c}24`,
                    borderBottom: `2px solid ${col.c}`,
                    padding: "15px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 12,
                      background: `${col.c}2e`,
                      border: `2px solid ${col.c}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 26,
                      fontWeight: 900,
                      color: col.c,
                    }}
                  >
                    {col.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 27,
                        fontFamily: FONTS.heading,
                        fontWeight: 900,
                        color: col.c,
                        textShadow: glow(col.c),
                      }}
                    >
                      {col.label}{" "}
                      <span style={{ fontSize: 18, color: COLORS.textMuted }}>· {col.tools.length}</span>
                    </div>
                    <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: COLORS.textMuted, letterSpacing: 1 }}>
                      {col.sub}
                    </div>
                  </div>
                </div>
                {/* tool list */}
                <div style={{ padding: "14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {col.tools.map((t, ti) => (
                    <div
                      key={ti}
                      style={{
                        fontSize: 16,
                        fontFamily: FONTS.body,
                        fontWeight: 500,
                        color: COLORS.textSecondary,
                        display: "flex",
                        gap: 9,
                        alignItems: "center",
                        padding: "8px 12px",
                        borderRadius: 9,
                        background: `${col.c}12`,
                        opacity: spAppear(frame, fps, 26 + ci * 7 + ti * 5),
                      }}
                    >
                      <span style={{ color: col.c, fontWeight: 900, fontSize: 14 }}>{col.icon}</span>
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Punchline
        text={
          <>
            Count the blades you{" "}
            <span style={{ color: COLORS.neonGreen }}>already own</span> first.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 96)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 09 — What EAKC already had ════════════ */
export const S09Already: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const tools = [
    { n: "query_eakc", d: "RAG retrieval + citations", i: "🔎" },
    { n: "grep_kb", d: "literal substring search", i: "🔠" },
    { n: "web_search", d: "Brave search", i: "🌐" },
    { n: "deep_research", d: "sub-topic fan-out", i: "🔬" },
    { n: "execute_code", d: "Railway Python sandbox", i: "💻" },
    { n: "generate", d: "grounded content", i: "✍️" },
    { n: "generate_artifact", d: "PDF · DOCX · PPTX", i: "📄" },
    { n: "entity_lookup", d: "knowledge-graph hops", i: "🕸️" },
    { n: "invoke_agent", d: "specialist delegation", i: "🤝" },
    { n: "ask_clarification", d: "clarifying questions", i: "❓" },
    { n: "send_email", d: "Gmail per-user OAuth", i: "📧" },
    { n: "missions + cron", d: "recurring runs", i: "🛰️" },
    { n: "agent memory", d: "cross-session episodes", i: "🧠" },
    { n: "blackboard", d: "shared scratchpad", i: "📋" },
    { n: "workflows", d: "tested code primitives", i: "⚙️" },
    { n: "learning tools", d: "quizzes · paths", i: "🎓" },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={9} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="EAKC already had the toolbox"
        subtitle="Verified, registered tools — the pivot is additive, not a rewrite"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 300,
          left: 110,
          right: 110,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 18,
          perspective: 1600,
        }}
      >
        {tools.map((t, i) => {
          const sp = spring({ frame: frame - 12 - i * 4, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          const sc = interpolate(sp, [0, 1], [0.55, 1], clamp);
          const row = Math.floor(i / 4);
          return (
            <div
              key={i}
              style={{
                height: 116,
                opacity: op,
                transform: `scale(${sc}) perspective(1200px) rotateY(${((i % 4) - 1.5) * 6}deg) translateZ(${Math.sin(frame * 0.03 + i) * 6}px)`,
                borderRadius: 14,
                background: `linear-gradient(140deg, ${accent}1e, ${COLORS.bgCard}f0)`,
                border: `2px solid ${accent}aa`,
                boxShadow: `0 0 20px ${accent}33`,
                padding: "14px 18px",
                display: "flex",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 38 }}>{t.i}</div>
              <div>
                <div
                  style={{
                    fontSize: 19,
                    fontFamily: FONTS.mono,
                    fontWeight: 800,
                    color: row % 2 ? COLORS.neonCyan : COLORS.neonGreen,
                    textShadow: glow(accent),
                  }}
                >
                  {t.n}
                </div>
                <div style={{ fontSize: 14, fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 3 }}>
                  {t.d}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ position: "absolute", bottom: 56, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <DefBox
          term="MCP server"
          body="Model Context Protocol — the open standard that lets any compliant AI client call EAKC's tools directly, no custom glue."
          color={accent}
          appear={spAppear(frame, fps, 78)}
          width={1080}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 10 — The real gaps ════════════ */
export const S10Gaps: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPink;
  const ap = spAppear(frame, fps);

  const gaps = [
    {
      icon: "👁️",
      title: "vision_analyze",
      body: "Reason over diagrams, charts and screenshots — not just OCR the text out of them.",
      c: COLORS.neonPink,
    },
    {
      icon: "🔊",
      title: "Text-to-Speech",
      body: "Read answers aloud and ship audio mission digests — accessibility and consume-on-the-go.",
      c: COLORS.neonPurple,
    },
    {
      icon: "📰",
      title: "web_extract",
      body: "Fetch and clean a full page of text — research needs more than search snippets.",
      c: COLORS.neonBlue,
    },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={10} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="The gaps worth building"
        subtitle="Only the pieces that fit a multi-tenant knowledge product"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 312,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 44,
          perspective: 1700,
        }}
      >
        {gaps.map((g, i) => (
          <Card3D key={i} inline w={420} h={290} color={g.c} frame={frame} delay={18 + i * 16} fps={fps} rotY={(i - 1) * 8}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%", gap: 14 }}>
              <div style={{ fontSize: 88, marginTop: 6 }}>{g.icon}</div>
              <div
                style={{
                  fontSize: 27,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: g.c,
                  textShadow: glow(g.c, true),
                }}
              >
                {g.title}
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  textAlign: "center",
                  lineHeight: 1.4,
                }}
              >
                {g.body}
              </div>
            </div>
          </Card3D>
        ))}
      </div>

      {/* out-of-scope strip */}
      <div
        style={{
          position: "absolute",
          top: 656,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 14,
          opacity: spAppear(frame, fps, 66),
        }}
      >
        <div style={{ fontSize: 15, fontFamily: FONTS.mono, color: COLORS.textMuted, letterSpacing: 2 }}>
          DELIBERATELY OUT OF SCOPE →
        </div>
        {["🖥️ host terminal", "📁 local file system", "🏠 home assistant"].map((t) => (
          <div
            key={t}
            style={{
              padding: "8px 18px",
              borderRadius: 999,
              border: `1.5px solid ${COLORS.textMuted}`,
              color: COLORS.textMuted,
              fontSize: 16,
              fontFamily: FONTS.mono,
              textDecoration: "line-through",
            }}
          >
            {t}
          </div>
        ))}
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="Add the blades that fit a team knowledge tool. Skip the ones that would let it touch a computer's hard drive — dangerous and pointless here."
          appear={spAppear(frame, fps, 80)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 11 — The big idea: the Skill ════════════ */
export const S11BigIdea: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonYellow;
  const ap = spAppear(frame, fps);

  const collapse = interpolate(frame, [30, 90], [0, 1], clamp);
  const bookSp = spring({ frame: frame - 80, fps, config: SPRING.punch });
  const bookScale = interpolate(bookSp, [0, 1], [0, 1], clamp);

  const notebooks = [
    { icon: "📝", from: { x: 260, y: 360 } },
    { icon: "⚙️", from: { x: 1480, y: 360 } },
    { icon: "📋", from: { x: 260, y: 680 } },
    { icon: "🔧", from: { x: 1480, y: 680 } },
  ];
  const centerX = 960;
  const centerY = 520;

  return (
    <SceneShell accent={accent}>
      <SlideChip n={11} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="The big idea — one Skill"
        subtitle="Four scattered notebooks collapse into a single source of truth"
        color={accent}
        appear={ap}
      />

      {/* collapsing notebooks */}
      {notebooks.map((nb, i) => {
        const x = interpolate(collapse, [0, 1], [nb.from.x, centerX], clamp);
        const y = interpolate(collapse, [0, 1], [nb.from.y, centerY], clamp);
        const op = interpolate(collapse, [0.55, 1], [1, 0], clamp) * spAppear(frame, fps, 8 + i * 5);
        const rot = interpolate(collapse, [0, 1], [0, 360], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x - 44,
              top: y - 44,
              fontSize: 84,
              opacity: op,
              transform: `rotate(${rot}deg) scale(${interpolate(collapse, [0, 1], [1, 0.3], clamp)})`,
              filter: `drop-shadow(0 0 18px ${accent}aa)`,
            }}
          >
            {nb.icon}
          </div>
        );
      })}

      {/* the one book */}
      <div
        style={{
          position: "absolute",
          left: centerX - 150,
          top: centerY - 150,
          width: 300,
          height: 300,
          transform: `scale(${bookScale}) rotate(${Math.sin(frame * 0.03) * 5}deg)`,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accent}55 0%, transparent 70%)`,
            transform: `scale(${1.4 + Math.sin(frame * 0.08) * 0.15})`,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <div style={{ fontSize: 130, filter: `drop-shadow(0 0 30px ${accent})` }}>📕</div>
          <div
            style={{
              fontSize: 36,
              fontFamily: FONTS.heading,
              fontWeight: 900,
              color: accent,
              textShadow: glow(accent, true),
            }}
          >
            SKILL
          </div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          top: 766,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 44,
        }}
      >
        <DefBox
          term="Skill"
          body="A small, durable, human-readable lesson EAKC stores after it works something out — so it never has to work it out twice."
          color={accent}
          appear={spAppear(frame, fps, 100)}
          width={680}
        />
        <PlainBox
          body="Instead of four notebooks, one notebook. Anything the system learns becomes a new page everyone can read."
          appear={spAppear(frame, fps, 116)}
          width={560}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 12 — Anatomy of a skill ════════════ */
export const S12Anatomy: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonCyan;
  const ap = spAppear(frame, fps);

  const fields = [
    { k: "tenant", v: "acme-corp", c: COLORS.neonBlue },
    { k: "slug", v: "exclude-medical-glean", c: COLORS.neonCyan },
    { k: "kind", v: "persona-rule", c: COLORS.neonPink },
    { k: "state", v: "active", c: COLORS.neonGreen },
    { k: "pinned", v: "false", c: COLORS.neonYellow },
    { k: "attached_to[]", v: '["Glean Researcher"]', c: COLORS.neonPurple },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={12} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="Anatomy of a skill"
        subtitle="One row in one table — inspectable, versioned, multi-tenant"
        color={accent}
        appear={ap}
      />

      {/* exploded 3D field stack */}
      <div style={{ position: "absolute", top: 300, left: 170, width: 720, height: 470, perspective: 1500 }}>
        <div style={{ transform: "rotateX(20deg) rotateY(16deg)", transformStyle: "preserve-3d" }}>
          {fields.map((f, i) => {
            const sp = spring({ frame: frame - 16 - i * 9, fps, config: SPRING.bouncy });
            const op = interpolate(sp, [0, 1], [0, 1], clamp);
            const tx = interpolate(sp, [0, 1], [-220, 0], clamp);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: i * 72,
                  left: tx,
                  width: 620,
                  height: 58,
                  opacity: op,
                  transform: `translateZ(${i * 26 + Math.sin(frame * 0.03 + i) * 8}px)`,
                  borderRadius: 12,
                  background: `linear-gradient(135deg, ${f.c}33, ${COLORS.bgCard}f2)`,
                  border: `2px solid ${f.c}`,
                  boxShadow: `0 0 22px ${f.c}66`,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 22px",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 19,
                    fontFamily: FONTS.mono,
                    fontWeight: 800,
                    color: f.c,
                    minWidth: 200,
                    textShadow: glow(f.c),
                  }}
                >
                  {f.k}
                </div>
                <div style={{ fontSize: 18, fontFamily: FONTS.mono, color: COLORS.textSecondary }}>{f.v}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* example skill body */}
      <div style={{ position: "absolute", top: 312, right: 120, width: 680 }}>
        <Card3D w={680} h={232} color={COLORS.neonPink} frame={frame} delay={40} fps={fps} inline rotY={-7}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Pill text="kind: persona-rule" color={COLORS.neonPink} size={15} />
            <div style={{ fontSize: 14, fontFamily: FONTS.mono, color: COLORS.textMuted }}>body — markdown</div>
          </div>
          <div
            style={{
              fontSize: 21,
              fontFamily: FONTS.body,
              color: COLORS.textPrimary,
              lineHeight: 1.5,
              fontStyle: "italic",
            }}
          >
            "When researching the product <b>Glean</b>, exclude medical-device
            results — confirm the result domain (glean.com) before citing it."
          </div>
          <div style={{ marginTop: 14, fontSize: 15, fontFamily: FONTS.mono, color: COLORS.textMuted }}>
            ↳ readable English · undoable · attached to one agent
          </div>
        </Card3D>

        <div style={{ marginTop: 20 }}>
          <DefBox
            term="attached_to[]"
            body="The agents or steps a skill is wired into. In EAKC today, attachment is the selection — an attached, active skill is always injected."
            color={accent}
            appear={spAppear(frame, fps, 60)}
            width={680}
          />
        </div>
      </div>

      <Punchline
        text={
          <>
            A skill is something you can{" "}
            <span style={{ color: accent }}>open and read</span>.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 88)}
      />
    </SceneShell>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
